"use client";

import { is_email, is_mobile } from "@core/helpers/regex";
import { parse_local_cart } from "@core/helpers/local-cart";
import {
  change_password_mutation,
  get_current_user_query,
  get_current_user_tutorials_query,
  has_user_mutation,
  login_mutation,
  login_send_sms_mutation,
  register_mutation,
  register_send_sms_mutation,
  reset_password_send_email_mutation,
  reset_password_send_sms_mutation,
  reset_password_verify_mutation,
  sms_login_verify_token_mutation,
  update_company_mutation,
  update_user_identity_mutation,
  update_user_mobile_send_sms_mutation,
  update_user_mobile_send_sms_v2_mutation,
  update_user_mobile_verify_current_sms_mutation,
  update_user_mobile_verify_sms_mutation,
  update_user_mutation,
} from "@core/services/auth";
import { toast_configs } from "@core/constants/toast_configs";
// every message below is written in Persian and shown in the visitor's language (core-messages.js)
import { core_message } from "@core/helpers/core-messages";
import { PASSWORD_MIN_LENGTH } from "@core/constants/password";
import { useCallback, useEffect, useMemo, useState } from "react";
import useCountDown from "./useCountDown";
import useForm from "./useForm";
import { is_function } from "@core/helpers/function";
import { get_cookie, set_cookie } from "@core/helpers/cookie";
import { useAuthContext } from "@core/helpers/client/contexts/AuthContext";
import { check_validation_with_form } from "../validations";
// birthdays are read and written on Iran's calendar day, so a browser outside Iran no longer moves them by one day
import { birthday_to_jalali_parts, jalali_birthday_to_timestamp } from "@core/helpers/birthday";
// import { useTranslations } from "next-intl";
import { sync_local_cart_mutation } from "@core/services/products";
import useCapToken from "./useCapToken";

const validationRules = {
  username: {
    regex: /(^09\d{9}$)|(^[^\s@]+@[^\s@]+\.[^\s@]+$)/,
    get message() { return core_message("ایمیل یا شماره موبایل معتبر نیست."); },
  },

  nickname: {
    regex: /^.{3,}$/,
    get message() { return core_message("نام باید حداقل ۳ کاراکتر باشد."); },
  },

  phone: {
    regex: /^09\d{9}$/,
    get message() { return core_message(" شماره موبایل وارد شده صحیح نیست "); },
  },

  email: {
    regex: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
    get message() { return core_message("ایمیل وارد شده معتبر نیست."); },
  },

  password: {
    // same rule as the submit handlers (it asked for 5 characters with mixed case and a digit, which nothing else did)
    regex: new RegExp(`^.{${PASSWORD_MIN_LENGTH},}$`),
    get message() { return core_message("رمز عبور می بایست حداقا 3 کارکتر داشته باشد"); },
  },
};

const useAuth = ({ countdown_className, custom_user_gql } = {}) => {
  const { get_captcha_token } = useCapToken();
  // const t = useTranslations();
  const {
    set_token,
    user_loading,
    set_user_loading,
    set_is_reloading,
    user,
    set_cart,
    set_user,
  } = useAuthContext();
  const [register_with_email, set_register_with_email] = useState(false);
  const [has_been_registered, set_has_been_registered] = useState(false);
  const [loading, set_loading] = useState({});
  const [tutorials, set_tutorials] = useState([]);
  const [company, set_company] = useState({});
  const { set_counter_down, counter_down, convert_countdown_time } =
    useCountDown({ countdown_className });
  const [form, set_form, form_on_change] = useForm();

  const auth_process = async (data) => {
    // console.log('data',data)
    set_cookie("_access_id", data?.user?.access_id);
    set_cookie("_token", data?.token);
    set_cookie("_popup_view_count", JSON.stringify({}));
    set_token(data?.token);
    const local_cart = parse_local_cart(await get_cookie("local-cart"));
    const result = await sync_local_cart_mutation({
      input: { products: local_cart, is_inquiry: 0 },
    });
    if (result?.status) {
      set_cookie("local-cart", JSON.stringify([]));
    }
    set_is_reloading(true);
  };

  const logout_process = (data) => {
    set_cookie("_token", "");
    set_cookie("_access_id", "");
    set_token("");
    set_cart([]);
    set_user(null);
  };

  const register_send_sms_handler = useCallback(
    async (args) => {
      const { success_callback, error_callback, check_details = false } = args;
      let valid = true;
      let errors = {};
      if (check_details) {
        if ((form?.name || '')?.length === 0) {
          valid = false;
          errors['name'] = core_message("نام را وارد کنید.")
        }
        if ((form?.name || '')?.length > 0 && form?.name?.length < 3) {
          valid = false;
          errors['name'] = core_message("نام باید بیشتر از 3 کاراکتر باشد.")
        }
        if ((form?.last_name || '')?.length === 0) {
          valid = false;
          errors['last_name'] = core_message("نام خانوادگی را وارد کنید.")
        }
        if ((form?.last_name || '')?.length > 0 && form?.last_name?.length < 3) {
          valid = false;
          errors['last_name'] = core_message("نام خانوادگی باید بیشتر از 3 کاراکتر باشد.")
        }
        if (Object.keys(errors)?.length > 0 && !valid) {
          set_form(prev => ({ ...(prev || {}), errors }))
        }

      }

      if (valid) {

        set_loading((prev) => ({ ...prev, resend_sms: true }));
        const captcha = await get_captcha_token({ place: "register" });
        const result = await register_send_sms_mutation({
          input: { captcha, username: form?.username, mobile: form?.username, name: form?.name || '', last_name: form?.last_name || '' },
        });
        if (result.status) {
          set_counter_down(120);
          if (is_function(success_callback)) {
            success_callback(result?.data);
          }
        } else {
          if (is_function(error_callback)) {
            error_callback(result?.errors);
          }
        }
        set_loading((prev) => ({ ...prev, resend_sms: false }));
        return result?.status || false;
      }
      return false;
    },
    [form?.username, form?.name, form?.last_name]
  );

  const login_send_sms_handler = useCallback(
    async (args) => {
      const {
        success_callback,
        error_callback,
        create_if_not_exists = false,
      } = args;
      set_loading((prev) => ({ ...prev, resend_sms: true }));
      const captcha = await get_captcha_token({ place: "login" });
      const result = await login_send_sms_mutation({
        captcha,
        mobile: form?.username,
        create_if_not_exists,
      });
      if (result.status) {
        set_counter_down(120);
        if (is_function(success_callback)) {
          success_callback(result?.data);
        }
      } else {
        if (is_function(error_callback)) {
          error_callback(result?.errors);
        }
      }
      set_loading((prev) => ({ ...prev, resend_sms: false }));
      return result?.status || false;
    },
    [form?.username]
  );

  const resend_code = useCallback(
    async (args, check_register = true) => {
      if (has_been_registered || !check_register) {
        await login_send_sms_handler(args);
      } else {
        await register_send_sms_handler(args);
      }
    },
    [has_been_registered, login_send_sms_handler, register_send_sms_handler]
  );

  const check_user_has_registered = useCallback(
    async (e, args) => {
      if (e) {
        e?.preventDefault?.();
      }
      const { success_callback, error_callback, check_with_send_sms = true } = args;
      set_loading((prev) => ({ ...prev, check_user: true }));

      const captcha = await get_captcha_token({ place: ["login", "register"], consume: false });
      const result = await has_user_mutation({
        input: { captcha, username: form?.username },
      });
      const is_mobile = form?.username?.match(/^\d+$/);
      // هندلرهای ارسال پیامک نباید success_callback مرحله را صدا بزنند،
      // وگرنه callback دو بار (یک‌بار با نتیجه‌ی پیامک) اجرا می‌شود.
      const sms_args = { ...args, success_callback: undefined };
      if (result?.status) {
        if (result?.data === false) {
          if (is_mobile) {
            if (check_with_send_sms) {
              register_send_sms_handler(sms_args);
            }
          }
          set_has_been_registered(false);
        } else {
          set_has_been_registered(true);
          if (is_mobile) {
            // اگر ارسال کد یکبار مصرف ناموفق باشد نباید کاربر را به مرحله‌ی
            // وارد کردن کد بفرستیم، وگرنه منتظر پیامکی می‌ماند که هرگز نمی‌رسد.
            const sms_sent = await login_send_sms_handler(sms_args);
            if (sms_sent === false) {
              set_form((prev) => ({
                ...(prev || {}),
                errors: {
                  ...(prev?.errors || {}),
                  username:
                    core_message("ارسال کد یکبار مصرف انجام نشد. دوباره تلاش کنید یا با رمز عبور وارد شوید."),
                },
              }));
              set_loading((prev) => ({ ...prev, check_user: false }));
              return;
            }
          }
        }
        if (is_mobile) {
          set_register_with_email(false);
        } else {
          set_register_with_email(true);
        }
        // handleTabClick("tab2");
        if (is_function(success_callback)) {
          success_callback(result?.data);
        }
      } else {
        if (is_function(error_callback)) {
          error_callback(result?.errors);
        }
      }
      set_loading((prev) => ({ ...prev, check_user: false }));
    },
    [form?.username, register_send_sms_handler, login_send_sms_handler]
  );

  const login_handler = useCallback(
    async (e, args) => {
      const { success_callback, error_callback } = args;
      e?.preventDefault?.();
      set_loading((prev) => ({ ...prev, submit_email: true }));

      const captcha = await get_captcha_token({ place: "login" });
      const result = await login_mutation({
        input: { captcha, username: form?.username, password: form?.password },
      });
      if (result.status) {
        auth_process(result?.data);
        if (is_function(success_callback)) {
          success_callback(result?.data);
        }
      } else {
        if (result?.errors?.[0]?.message?.includes("login_error1")) {
          set_form((prev) => ({
            ...prev,
            errors: {
              ...(prev?.errors || {}),
              // username: t("validations.username_not_found"),
              username: core_message("کاربری با این مشخصات پیدا نشد."),
            },
          }));
        } else if (result?.errors?.[0]?.message?.includes("login_error3")) {
          set_form((prev) => ({
            ...prev,
            errors: {
              ...(prev?.errors || {}),
              // password: t("validations.incorrect_password"),
              password: core_message("رمزعبور وارد شده صحیح نیست"),
              // "رمز عبور وارد شده صحیح نیست",
            },
          }));
        } else {
          // بدون این حالت، هر خطای ناشناخته‌ای باعث می‌شد ورود بی‌هیچ پیامی شکست بخورد.
          set_form((prev) => ({
            ...prev,
            errors: {
              ...(prev?.errors || {}),
              password: core_message("ورود انجام نشد. شماره موبایل و رمز عبور را بررسی کنید."),
            },
          }));
        }
        if (is_function(error_callback)) {
          error_callback(result?.errors);
        }
      }
      set_loading((prev) => ({ ...prev, submit_email: false }));
    },
    [form]
  );

  const register_handler = useCallback(
    async (e, args, partner_register) => {
      const { success_callback, error_callback } = args;

      e?.preventDefault?.();
      set_loading((prev) => ({ ...prev, submit_email: true }));
      const captcha = await get_captcha_token({ place: "register" });
      const result = await register_mutation({
        input: {
          captcha,
          is_partner: partner_register,
          password: form?.password,
          password_confirmation: form?.password_confirmation,
          email: is_email(form?.username)
            ? form?.username
            : form?.email || null,
          nickname: form?.nickname,
          name: form?.name,
          last_name: form?.last_name,
          mobile: form?.mobile,
          username: form?.username,
          national_code: form?.national_code,
          gender: form?.gender,
          referrer_code: form?.referrer_code,
          company: form?.company
            ? {
              title: form?.company?.title,
              country_id: "103",
              state_id: form?.company?.state_id,
              city_id: form?.company?.city_id,
              address: form?.company?.address,
            }
            : null,
        },
      });
      if (result.status) {
        auth_process(result?.data);
        if (is_function(success_callback)) {
          success_callback(result?.data);
        }
      } else {
        // toast_configs("لطفا مقادیر را به درستی وارد نمایید.", "error", {
        //   className: "toast-general-style",
        // });
        // toast_configs(t('validations.fill_fields_right'), 'error', { className: "toast-general-style" })

        if (result?.errors?.[0]?.message?.includes("password")) {
          set_form((prev) => ({
            ...prev,
            errors: {
              ...(prev?.errors || {}),
              // password: t("validations.incorrect_password"),
              password: core_message("رمزعبور وارد شده صحیح نیست"),
            },
          }));
        }

        const errors = {};
        Object.keys(result?.errors?.[0]?.errors || {})?.forEach((key) => {
          errors[key] = [];
          if (Array.isArray(result?.errors?.[0]?.errors?.[key])) {
            result?.errors?.[0]?.errors?.[key]?.forEach((error) => {
              errors[key].push(`validations.${error}`);
            });
          }
        });
        set_form((prev) => ({
          ...prev,
          errors: {
            ...(prev?.errors || {}),
            ...(errors || {}),
          },
        }));

        if (is_function(error_callback)) {
          error_callback(result?.errors);
        }
      }
      set_loading((prev) => ({ ...prev, submit_email: false }));
    },
    [form]
  );

  const sms_verify_handler = useCallback(
    async (e, args) => {
      const { success_callback, error_callback } = args;

      e?.preventDefault?.();
      set_loading((prev) => ({ ...prev, submit_sms: true }));

      // فقط مقادیر پرشده فرستاده می‌شوند؛ ارسال رشته‌ی خالی (مثلا nickname)
      // باعث خطای اعتبارسنجی سمت سرور و شکست ورود با کد یکبار مصرف می‌شد.
      const full_name = [form?.name, form?.last_name]
        .filter((part) => (part || "").trim().length > 0)
        .join(" ")
        .trim();
      const verify_input = {};
      if ((form?.nickname || "").trim().length > 0) {
        verify_input.nickname = form?.nickname;
      } else if (full_name.length > 0) {
        verify_input.nickname = full_name;
      }
      if ((form?.name || "").trim().length > 0) verify_input.name = form?.name;
      if ((form?.last_name || "").trim().length > 0) verify_input.last_name = form?.last_name;

      const captcha = await get_captcha_token({ place: ["login", "register"], consume: false, solve_if_missing: false });
      const result = await sms_login_verify_token_mutation({
        captcha,
        mobile: form?.username,
        token: form?.code?.replaceAll("-", "")?.replaceAll(" ", ""),
        input: Object.keys(verify_input).length > 0 ? verify_input : undefined,
      });
      if (result.status) {
        auth_process(result?.data);
        if (is_function(success_callback)) {
          success_callback(result?.data);
        }
      } else {
        if (result?.errors?.[0]?.message?.includes("token")) {
          set_form((prev) => ({
            ...prev,
            errors: {
              ...(prev?.errors || {}),
              code: core_message("کد وارد شده صحیح نیست"),
              // code: t("validations.incorrect_code"),
            },
          }));
        }
        if (is_function(error_callback)) {
          error_callback(result?.errors);
        }
      }
      set_loading((prev) => ({ ...prev, submit_sms: false }));
    },
    [form]
  );

  const login_or_register_send_sms_handler = useCallback(
    async (e, args) => {
      set_form((prev) => ({
        ...prev,
        nickname: "",
        password: "",
        code: "",
        password_confirmation: "",
      }));
      if (form?.username?.length > 0) {
        if (form?.username?.length < 3) {
          set_form((prev) => ({
            ...(prev || {}),
            errors: {
              ...(prev?.errors || {}),
              username: core_message(" ایمیل یا شماره تماس می باست حداقل 3 کاراکتر باشد"),
              // username: t("validations.email_mobile_min_length"),
            },
          }));
        } else {
          // console.log('form?.username?.match(/^\d+$/)', form?.username?.test(/^\d+$/), is_mobile(form?.username))
          if (form?.username?.match(/^\d+$/)) {
            if (!is_mobile(form?.username)) {
              set_form((prev) => ({
                ...(prev || {}),
                errors: {
                  ...(prev?.errors || {}),
                  username: core_message("فرمت شماره تلفن صحیح نیست"),
                  // username: t("validations.mobile_iranian_mobile"),
                },
              }));
            } else {
              login_send_sms_handler(args);
            }
          } else {
            set_form((prev) => ({
              ...(prev || {}),
              errors: {
                ...(prev?.errors || {}),
                username: core_message("فرمت شماره تلفن صحیح نیست"),
                // username: t("validations.mobile_iranian_mobile"),
              },
            }));
          }
        }
      } else {
        set_form((prev) => ({
          ...(prev || {}),
          errors: {
            ...(prev?.errors || {}),
            username: core_message("ایمیل یا شماره تماس را وارد کنید."),
          },
        }));
      }
    },
    [form, login_send_sms_handler]
  );

  const check_has_user_handler = useCallback(
    async (e, args = {}) => {
      if (e) {
        e?.preventDefault?.();
      }
      const { username_mode = 'mobile_and_email' } = args
      set_form((prev) => ({
        ...prev,
        nickname: "",
        password: "",
        code: "",
        password_confirmation: "",
      }));
      if (form?.username?.length > 0) {
        if (form?.username?.length < 3) {
          set_form((prev) => ({
            ...(prev || {}),
            errors: {
              ...(prev?.errors || {}),
              username: username_mode === 'mobile_and_email' ? core_message(" ایمیل یا شماره تماس می باست حداقل 3 کاراکتر باشد") : username_mode === 'mobile' ? core_message('شماره موبایل می باست حداقل 3 کاراکتر باشد') : core_message('ایمیل می باست حداقل 3 کاراکتر باشد'),
              // username: t("validations.email_mobile_min_length"),
            },
          }));
        } else {
          if (form?.username?.match(/^\d+$/)) {
            if (!is_mobile(form?.username)) {
              set_form((prev) => ({
                ...(prev || {}),
                errors: {
                  ...(prev?.errors || {}),
                  username: username_mode === "mobile" ? core_message("فرمت شماره موبایل صحیح نیست") : core_message("فرمت شماره تلفن صحیح نیست"),
                  // username: t("validations.mobile_iranian_mobile"),
                },
              }));
            } else {
              check_user_has_registered(e, args);
            }
          } else if (!is_email(form?.username)) {
            set_form((prev) => ({
              ...(prev || {}),
              errors: {
                ...(prev?.errors || {}),
                username: core_message("فرمت ایمیل صحیح نیست"),
                // username: t("validations.email_email"),
              },
            }));
          } else {
            check_user_has_registered(e, args);
          }
        }
      } else {
        set_form((prev) => ({
          ...(prev || {}),
          errors: {
            ...(prev?.errors || {}),
            username: username_mode === 'mobile_and_email' ? core_message("شماره تماس یا ایمیل را وارد کنید.") : username_mode === 'mobile' ? core_message("شماره موبایل را وارد کنید.") : core_message("ایمیل را وارد کنید."),
            // username: "validations.enter_mobile_email",
          },
        }));
      }
    },
    [form, check_user_has_registered]
  );

  const submit_with_sms_handler = useCallback(
    async (e, args = {}) => {
      e?.preventDefault?.();
      const { check_details = true } = args;
      if (!has_been_registered) {
        if (check_details) {
          if (
            (form?.nickname || "")?.length > 0 &&
            (form?.code || "")?.length > 0
          ) {
            if ((form?.nickname || "")?.length < 3) {
              set_form((prev) => ({
                ...(prev || {}),
                errors: {
                  ...(prev?.errors || {}),
                  nickname:
                    core_message("نام و نام خانوادگی می بایست حداقا 3 کارکتر داشته باشد"),
                  // nickname: t("validations.full_name_min_length_3"),
                },
              }));
            } else {
              await sms_verify_handler(e, args);
            }
          } else {
            if ((form?.nickname || "")?.length === 0) {
              set_form((prev) => ({
                ...(prev || {}),
                errors: {
                  ...(prev?.errors || {}),
                  nickname: core_message("لطفا نام و نام خانوادگی را وارد کنید"),
                  // nickname: t("validations.enter_full_name"),
                },
              }));
            }
            if ((form?.code || "")?.length === 0) {
              set_form((prev) => ({
                ...(prev || {}),
                errors: {
                  ...(prev?.errors || {}),
                  code: core_message("لطفا کد را وارد کنید"),
                  // code: t("validations.enter_code"),
                },
              }));
            }
          }
        } else {
          await sms_verify_handler(e, args);
        }
      } else {
        if ((form?.code || "")?.length > 0) {
          await sms_verify_handler(e, args);
        } else {
          set_form((prev) => ({
            ...(prev || {}),
            errors: {
              ...(prev?.errors || {}),
              code: core_message("لطفا کد را وارد کنید"),
              // code: t("validations.enter_code"),
            },
          }));
        }
      }
    },
    [has_been_registered, form, sms_verify_handler]
  );

  const submit_login_or_register_with_sms_handler = useCallback(
    async (e, args) => {
      e?.preventDefault?.();

      if ((form?.code || "")?.length > 0) {
        await sms_verify_handler(e, args);
      } else {
        set_form((prev) => ({
          ...(prev || {}),
          errors: {
            ...(prev?.errors || {}),
            code: core_message("لطفا کد را وارد کنید"),
            // code: t("validations.enter_code"),
          },
        }));
      }
    },
    [has_been_registered, form, sms_verify_handler]
  );

  const submit_with_email_handler = useCallback(
    async (e, args, check_register = true) => {
      e?.preventDefault?.();
      if (has_been_registered || !check_register) {
        if ((form?.password || "")?.length > 0) {
          if (form?.password?.length < PASSWORD_MIN_LENGTH) {
            set_form((prev) => ({
              ...(prev || {}),
              errors: {
                ...(prev?.errors || {}),
                password: core_message("رمز عبور می بایست حداقا 3 کارکتر داشته باشد"),
                // password: t("validations.password_min_length_3"),
              },
            }));
          } else {
            login_handler(e, args);
          }
        } else {
          set_form((prev) => ({
            ...(prev || {}),
            errors: {
              ...(prev?.errors || {}),
              password: core_message("لطفا رمزعبور را وارد کنید"),
              // password: t("validations.enter_password"),
            },
          }));
        }
      } else {
        let valid = true;
        if (form?.nickname?.length < 3 && form?.nickname?.length > 0) {
          valid = false;
          set_form((prev) => ({
            ...(prev || {}),
            errors: {
              ...(prev?.errors || {}),
              nickname: core_message("نام و نام خانوادگی می بایست حداقا 3 کارکتر داشته باشد"),
              // nickname: t("validations.full_name_min_length_3"),
            },
          }));
        } else if ((form?.nickname || "")?.length === 0) {
          valid = false;
          set_form((prev) => ({
            ...(prev || {}),
            errors: {
              ...(prev?.errors || {}),
              nickname: core_message("لطفا نام و نام خانوادگی را وارد کنید"),
              // nickname: t("validations.enter_full_name"),
            },
          }));
        }
        if (form?.password?.length < PASSWORD_MIN_LENGTH && form?.password?.length > 0) {
          valid = false;
          set_form((prev) => ({
            ...(prev || {}),
            errors: {
              ...(prev?.errors || {}),
              password: core_message("رمز عبور می بایست حداقا 3 کارکتر داشته باشد"),
              // password: t("validations.password_min_length_3"),
            },
          }));
        } else if ((form?.password || "")?.length === 0) {
          valid = false;
          set_form((prev) => ({
            ...(prev || {}),
            errors: {
              ...(prev?.errors || {}),
              password: core_message("لطفا رمزعبور را وارد کنید"),
              // password: t("validations.enter_password"),
            },
          }));
        }
        if (form?.password !== form?.password_confirmation) {
          valid = false;
          set_form((prev) => ({
            ...(prev || {}),
            errors: {
              ...(prev?.errors || {}),
              password_confirmation:
                core_message("رمز عبور و تکرار رمز عبور با هم برابر نیست"),
              // password_confirmation: t(
              //   "validations.password_confirmation_not_match"
              // ),
            },
          }));
        }
        if (valid) {
          await register_handler(e, args);
        }
      }
    },
    [has_been_registered, form, register_handler, login_handler]
  );

  const register_with_email_handler = useCallback(
    async (e, args, partner_register = false) => {
      e?.preventDefault?.();

      const { extra_validations = {} } = args;

      let validation_rules = {
        name: ["required", { type: "min_length", length: 3 }],
        last_name: ["required", { type: "min_length", length: 3 }],
        mobile: ["required", "iranian_mobile"],
        ...extra_validations,
      };

      if (partner_register) {
        validation_rules = {
          ...validation_rules,
          email: ["email"],
          national_code: ["required", "iranian_national_code"],
          "company.title": ["required"],
          "company.state_id": ["required"],
          "company.city_id": ["required"],
          "company.address": ["required"],
        };
      }

      let { has_error, errors, _errors } = await check_validation_with_form(
        validation_rules,
        form
      );
      if (has_error) {
        // toast_configs(t('validations.fill_fields_right'), 'error', { className: "toast-general-style" })
        set_form((prev) => ({
          ...prev,
          errors: { ...(_errors || {}) },
        }));
      }
      let valid = true;

      if (form?.password?.length < PASSWORD_MIN_LENGTH && form?.password?.length > 0) {
        // toast_configs(t('validations.fill_fields_right'), 'error', { className: "toast-general-style" })
        valid = false;
        set_form((prev) => ({
          ...(prev || {}),
          errors: {
            ...(prev?.errors || {}),
            password: core_message("رمز عبور می بایست حداقا 3 کارکتر داشته باشد"),
            // password: t("validations.password_min_length_3"),
          },
        }));
      } else if ((form?.password || "")?.length === 0) {
        // toast_configs(t('validations.fill_fields_right'), 'error', { className: "toast-general-style" })
        valid = false;
        set_form((prev) => ({
          ...(prev || {}),
          errors: {
            ...(prev?.errors || {}),
            password: core_message("لطفا رمزعبور را وارد کنید"),
            // password: t("validations.enter_password"),
          },
        }));
      } else if (
        form?.password !== form?.password_confirmation &&
        form?.password?.length > 0
      ) {
        // toast_configs(t('validations.fill_fields_right'), 'error', { className: "toast-general-style" })
        valid = false;
        set_form((prev) => ({
          ...(prev || {}),
          errors: {
            ...(prev?.errors || {}),
            password_confirmation: core_message("رمز عبور و تکرار رمز عبور با هم برابر نیست"),
            //  password_confirmation: t(
            //   "validations.password_confirmation_not_match"
            // ),
          },
        }));
      }
      if (valid && !has_error) {
        await register_handler(e, args, partner_register);
      } else {
        toast_configs(core_message("لطفا مقادیر را به درستی وارد نمایید."), "error", {
          className: "toast-general-style",
        });
      }
    },
    [form, register_handler]
  );

  const login_with_email_handler = useCallback(
    async (e, args, check_register = true, with_username = false) => {
      e?.preventDefault?.();
      const { username_mode = 'mobile_and_email' } = args
      if (has_been_registered || !check_register) {
        let valid = true;
        if (form?.username?.length > 0) {
          if (form?.username?.length < 3 && !form?.username?.match(/^\d+$/)) {
            valid = false;
            set_form((prev) => ({
              ...(prev || {}),
              errors: {
                ...(prev?.errors || {}),
                username: username_mode === 'mobile_and_email' ? core_message(" ایمیل یا شماره تماس می باست حداقل 3 کاراکتر باشد") : username_mode === 'mobile' ? core_message('شماره موبایل می باست حداقل 3 کاراکتر باشد') : core_message('ایمیل می باست حداقل 3 کاراکتر باشد'),
                // username: t("validations.email_mobile_min_length"),
              },
            }));
          } else {
            if (form?.username?.match(/^\d+$/)) {
              if (!is_mobile(form?.username) && !with_username) {
                valid = false;

                set_form((prev) => ({
                  ...(prev || {}),
                  errors: {
                    ...(prev?.errors || {}),
                    username: username_mode === "mobile" ? core_message("فرمت شماره موبایل صحیح نیست") : core_message("فرمت شماره تلفن صحیح نیست"),
                    // username: t("validations.mobile_iranian_mobile"),
                  },
                }));
              }
            } else if (!is_email(form?.username) && !with_username) {
              valid = false;

              set_form((prev) => ({
                ...(prev || {}),
                errors: {
                  ...(prev?.errors || {}),
                  username: core_message("فرمت ایمیل صحیح نیست"),
                  // username: t("validations.email_email"),
                },
              }));
            }
          }
        } else {
          valid = false;

          set_form((prev) => ({
            ...(prev || {}),
            errors: {
              ...(prev?.errors || {}),
              username: with_username
                ? "validations.enter_mobile_email_username"
                : username_mode === 'mobile_and_email' ? core_message("شماره تماس یا ایمیل را وارد کنید.") : username_mode === 'mobile' ? core_message("شماره موبایل را وارد کنید.") : core_message("ایمیل را وارد کنید."),
            },
          }));
        }
        if ((form?.password || "")?.length > 0) {
          if (form?.password?.length < PASSWORD_MIN_LENGTH) {
            valid = false;

            set_form((prev) => ({
              ...(prev || {}),
              errors: {
                ...(prev?.errors || {}),
                password: core_message("رمز عبور می بایست حداقا 3 کارکتر داشته باشد"),
                // password: t("validations.password_min_length_3"),
              },
            }));
          }
        } else {
          valid = false;

          set_form((prev) => ({
            ...(prev || {}),
            errors: {
              ...(prev?.errors || {}),
              password: core_message("لطفا رمزعبور را وارد کنید"),
              // password: t("validations.enter_password"),
            },
          }));
        }
        if (valid) {
          login_handler(e, args);
        }
      }
    },
    [has_been_registered, form, login_handler]
  );

  const forgot_password_handler = useCallback(
    async (e, args) => {
      const { success_callback, error_callback } = args;

      e?.preventDefault?.();
      set_loading((prev) => ({ ...prev, send_email_code: true }));
      const result = await reset_password_send_email_mutation({
        email: form?.username,
      });
      if (result.status) {
        if (is_function(success_callback)) {
          set_counter_down(120);
          success_callback(result?.data);
        }
      } else {
        if (is_function(error_callback)) {
          error_callback(result?.errors);
        }
      }
      set_loading((prev) => ({ ...prev, send_email_code: false }));
      // setActiveTab("tab3");
    },
    [form?.username]
  );

  const reset_password_verify_handler = useCallback(
    async (e, args) => {
      const { success_callback, error_callback, has_password = true, has_code = true } = args;
      e?.preventDefault?.();
      let valid = true;
      const token = form?.code?.replaceAll("-", "")?.replaceAll(" ", "");
      if (has_password) {
        if (form?.password?.length < PASSWORD_MIN_LENGTH) {
          valid = false;
          set_form((prev) => ({
            ...(prev || {}),
            errors: {
              ...(prev?.errors || {}),
              password: core_message("رمز عبور می بایست حداقا 3 کارکتر داشته باشد"),
              // password: t("validations.password_min_length_3"),
            },
          }));
        } else if ((form?.password || "")?.length === 0) {
          valid = false;
          set_form((prev) => ({
            ...(prev || {}),
            errors: {
              ...(prev?.errors || {}),
              password: core_message("لطفا رمزعبور را وارد کنید"),
              // password: t("validations.enter_password"),
            },
          }));
        }
        if (form?.password !== form?.password_confirmation) {
          valid = false;
          set_form((prev) => ({
            ...(prev || {}),
            errors: {
              ...(prev?.errors || {}),
              password_confirmation: core_message("رمز عبور و تکرار رمز عبور با هم برابر نیست"),
              // password_confirmation: t(
              //   "validations.password_confirmation_not_match"
              // ),
            },
          }));
        }
      }
      if ((token || "")?.length === 0 && has_code) {
        valid = false;
        set_form((prev) => ({
          ...(prev || {}),
          errors: {
            ...(prev?.errors || {}),
            code: core_message("کد ارسال شده را وارد کنید."),
          },
        }));
      }
      if (valid) {
        set_loading((prev) => ({ ...prev, reset_password: true }));
        const result = await reset_password_verify_mutation({
          input: {
            email: is_email(form?.username)
              ? form?.username
              : form?.email || null,
            mobile: args?.mobile || form?.mobile,
            token: token,
            password: has_password ? form?.password : undefined,
            password_confirmation: has_password ? form?.password_confirmation : undefined,
            reset_token: form?.reset_token || undefined
          },
        });
        if (result?.status) {
          set_form(prev => ({ ...(prev || {}), reset_token: result?.data?.extra_data?.reset_token }))
          if (is_function(success_callback)) {
            success_callback(result?.data);
          }
        } else {
          if (
            Object.keys(result?.errors?.[0]?.errors || {})?.includes("token")
          ) {
            set_form((prev) => ({
              ...prev,
              errors: {
                ...(prev?.errors || {}),
                code: core_message("کد وارد شده صحیح نیست"),
                // code: t("validations.incorrect_code"),
              },
            }));
          }
          if (is_function(error_callback)) {
            error_callback(result?.errors);
          }
        }
        set_loading((prev) => ({ ...prev, reset_password: false }));
      }
    },
    [form]
  );

  const init_birtyday = useCallback((birthday) => {
    if (birthday) {
      const parts = birthday_to_jalali_parts(birthday);
      if (parts) return parts;
    }
    return {};

  }, [])

  const get_user_handler = useCallback(async (args = {}) => {
    const { success_callback, error_callback } = args;
    set_user_loading(true);
    const result = await get_current_user_query({
      custom_gql: custom_user_gql,
    });
    if (result.status) {
      set_company(result?.data?.companies?.[0]);
      set_form((prev) => ({
        ...prev,
        ...result?.data,
        company: result?.data?.companies?.[0],
        ...init_birtyday(result?.data?.birthday)
      }));
      if (result.status) {
        if (is_function(success_callback)) {
          success_callback(result?.data);
        }
      } else {
        if (is_function(error_callback)) {
          error_callback(result?.errors);
        }
      }
    }
    set_user_loading(false);
  }, []);

  const update_user_handler = useCallback(
    async (e, args = {}) => {
      e?.preventDefault?.();
      const {
        success_callback,
        error_callback,
        required_inputs = [core_message("ایمیل"), core_message("نام کاربری"), core_message("شماره موبایل")],
      } = args;
      const validation_rules = {
        name: ["required", { type: "min_length", length: 3 }],
        last_name: ["required", { type: "min_length", length: 3 }],
        username: [{ type: "min_length", length: 3 }],
        email: ["email", { type: "min_length", length: 3 }],
        mobile: ["iranian_mobile"],
      };
      let { has_error, errors } = await check_validation_with_form(
        validation_rules,
        form
      );
      set_form((prev) => ({ ...prev, errors }));
      let valid = true;
      if (
        (form?.email || "")?.length === 0 &&
        (form?.mobile || "")?.length === 0 &&
        (form?.username || "")?.length === 0
      ) {
        valid = false;
        toast_configs(
          core_message("حداقل یکی از فیلد های :fields الزامی است", { fields: required_inputs?.join(` ${core_message("یا")} `) }),
          "error",
          { className: "toast-general-style" }
        );
      }
      let birthday = null;
      if (form?.year && form?.month && form?.day) {
        if (parseFloat(form?.year) < 1300) {
          valid = false;
          set_form((prev) => ({
            ...(prev || {}),
            errors: {
              ...(prev?.errors || {}),
              year: [core_message("سال تولد را به درستی وارد کنید")],
            },
          }));
        } else {
          const month =
            form?.month?.toString()?.length === 1
              ? `0${form?.month}`
              : form?.month;
          const day =
            form?.day?.toString()?.length === 1 ? `0${form?.day}` : form?.day;
          birthday = jalali_birthday_to_timestamp(form?.year, month, day);
        }
      }
      // console.log('birthday', birthday)
      if (!has_error && valid) {
        set_loading((prev) => ({ ...prev, update_user: true }));

        const result = await update_user_mutation({
          input: {
            name: form?.name,
            last_name: form?.last_name,
            username: form?.username,
            email: form?.email,
            mobile: form?.mobile,
            user_information: {
              gender: form?.gender ? parseInt(form?.gender) : null,
              birthday,
              nickname: form?.nickname,
            },
          },
        });

        if (result.status) {
          if (is_function(success_callback)) {
            success_callback(result?.data);
          }
        } else {
          if (is_function(error_callback)) {
            if (result?.errors?.[0]?.errors) {
              const obj = {};
              Object.keys(result?.errors?.[0]?.errors)?.forEach((key) => {
                obj[key] = [];
                obj[key].push(
                  ...result?.errors?.[0]?.errors?.[key]?.map((item) =>
                    item === "not_unique_error||email"
                      ? core_message("ایمیل از قبل وجود دارد")
                      : item === "not_unique_error||mobile"
                        ? core_message("شماره موبایل از قبل وجود دارد")
                        : `validations.${item}`
                  )
                );
              });
              set_form((prev) => ({
                ...(prev || {}),
                errors: {
                  ...(obj || {}),
                },
              }));
            }
            error_callback(result?.errors);
          }
        }
        set_loading((prev) => ({ ...prev, update_user: false }));
      }
    },
    [form]
  );

  const birthday_validation = useCallback((form_birthday) => {
    let birthday = null;
    let valid = true;
    if (form_birthday?.year && form_birthday?.month && form_birthday?.day) {
      if (parseFloat(form_birthday?.year) < 1300) {
        valid = false;
        set_form((prev) => ({
          ...(prev || {}),
          errors: {
            ...(prev?.errors || {}),
            year: [core_message("سال تولد را به درستی وارد کنید")],
          },
        }));

      } else {
        const month =
          form_birthday?.month?.toString()?.length === 1
            ? `0${form_birthday?.month}`
            : form_birthday?.month;
        const day =
          form_birthday?.day?.toString()?.length === 1 ? `0${form_birthday?.day}` : form_birthday?.day;
        birthday = jalali_birthday_to_timestamp(form_birthday?.year, month, day);
      }
    }
    return {
      valid,
      birthday
    }
  }, [])

  const update_company_handler = useCallback(
    async (e, args) => {
      e?.preventDefault?.();
      const validation_rules = {
        "company.title": ["required"],
        "company.registration_id": ["required"],
        "company.national_id": ["required", "iranian_national_id"],
        "company.address": ["required"],
        "company.city_id": ["required"],
        "company.state_id": ["required"],
        "company.phone": ["iranian_phone"],
        "company.postal_code": ["required", "iranian_postal_code"],
      };
      let { has_error, errors, _errors } = await check_validation_with_form(
        validation_rules,
        form
      );
      set_form((prev) => ({
        ...prev,
        errors: { ...(_errors || {}) },
      }));

      const { success_callback, error_callback } = args;
      if (!has_error) {
        set_loading((prev) => ({ ...prev, update_company: true }));

        let _form = { ...(form?.company || {}) };
        delete _form["id"];
        delete _form["city"];
        delete _form["state"];
        delete _form["is_verified"];

        const result = await update_company_mutation({
          id: company?.id,
          input: {
            ...(_form || {}),
          },
        });

        if (result.status) {
          if (is_function(success_callback)) {
            success_callback(result?.data);
          }
        } else {
          if (is_function(error_callback)) {
            error_callback(result?.errors);
          }
        }
        set_loading((prev) => ({ ...prev, update_company: false }));
      }
    },
    [form, company]
  );

  const change_password_handler = useCallback(
    async (e, args, check_current_password) => {
      const { success_callback, error_callback } = args;
      // `set_password` too when the visitor creates a first password (no current one to check); pages showing that
      // form read it and it was never set
      const creates_password = !check_current_password || !user?.has_password;
      set_loading((prev) => ({ ...prev, change_password: true, ...(creates_password ? { set_password: true } : {}) }));
      const result = await change_password_mutation({
        input: {
          old_password: check_current_password
            ? user?.has_password
              ? form?.old_password
              : null
            : null,
          password: form?.password,
          password_confirmation: form?.password_confirmation,
        },
      });

      if (result.status) {
        if (is_function(success_callback)) {
          success_callback(result?.data);
        }
      } else {
        if (result?.errors?.[0]?.errors?.old_password) {
          set_form((prev) => ({
            ...(prev || {}),
            errors: {
              ...(prev?.errors || {}),
              old_password: core_message("رمز فعلی صحیح نیست"),
            },
          }));
        }
        if (is_function(error_callback)) {
          error_callback(result?.errors);
        }
      }
      set_loading((prev) => ({ ...prev, change_password: false, ...(creates_password ? { set_password: false } : {}) }));
    },
    [form, user]
  );

  const reset_password_send_sms_handler = useCallback(
    async (e, args) => {
      const { success_callback, error_callback, mobile: mobile_arg } = args;
      // فراخوان می‌تواند شماره را صریح بدهد تا به تازه‌بودنِ state وابسته نباشیم
      const mobile = mobile_arg || form?.mobile;
      let valid = true;
      if (mobile?.length > 0) {
        if (!is_mobile(mobile)) {
          valid = false;
          set_form((prev) => ({
            ...(prev || {}),
            errors: {
              ...(prev?.errors || {}),
              mobile: core_message("فرمت شماره تلفن صحیح نیست"),
              // mobile: t("validations.mobile_iranian_mobile"),
            },
          }));
        }
      } else {
        valid = false;
        set_form((prev) => ({
          ...(prev || {}),
          errors: {
            ...(prev?.errors || {}),
            mobile: core_message("لطفا شماره تماس را وارد کنید"),
            // mobile: t("validations.enter_mobile"),
          },
        }));
      }
      if (valid) {
        set_loading((prev) => ({ ...prev, reset_password: true }));
        const result = await reset_password_send_sms_mutation({
          mobile,
        });

        if (result.status) {
          if (is_function(success_callback)) {
            set_counter_down(120);

            success_callback(result?.data);
          }
        } else {
          if (
            result?.errors?.[0]?.message === "not_found||mobile" ||
            result?.errors?.[0]?.message === "not_found||email"
          ) {
            set_form((prev) => ({
              ...(prev || {}),
              errors: {
                ...(prev?.errors || {}),
                // mobile: t("validations.username_not_found"),
                mobile: core_message("کاربری با این مشخصات پیدا نشد."),
              },
            }));
          }
          if (is_function(error_callback)) {
            error_callback(result?.errors);
          }
        }
        set_loading((prev) => ({ ...prev, reset_password: false }));
      }
    },
    [form]
  );

  const submit_change_password_handler = useCallback(
    async (e, args, check_current_password = true) => {
      e?.preventDefault?.();

      let valid = true;
      if (user?.has_password && check_current_password) {
        if (form?.old_password?.length === 0) {
          valid = false;
          set_form((prev) => ({
            ...(prev || {}),
            errors: {
              ...(prev?.errors || {}),
              old_password: core_message("رمز فعلی الزامی است"),
            },
          }));
        }
      }
      if (form?.password?.length < PASSWORD_MIN_LENGTH && form?.password?.length > 0) {
        valid = false;
        set_form((prev) => ({
          ...(prev || {}),
          errors: {
            ...(prev?.errors || {}),
            password: core_message("رمز عبور می بایست بیشتر از 2 کاراکتر باشد"),
          },
        }));
      } else if ((form?.password || "")?.length === 0) {
        valid = false;
        set_form((prev) => ({
          ...(prev || {}),
          errors: {
            ...(prev?.errors || {}),
            password: core_message("لطفا رمز عبور را وارد کنید"),
          },
        }));
      }
      if (form?.password !== form?.password_confirmation) {
        valid = false;
        set_form((prev) => ({
          ...(prev || {}),
          errors: {
            ...(prev?.errors || {}),
            password_confirmation: core_message("رمز عبور و تکرار رمز عبور با هم برابر نیست"),
          },
        }));
      }

      if (valid) {
        await change_password_handler(e, args, check_current_password);
      }
    },
    [change_password_handler]
  );

  const resend_email_code = useCallback(
    async (e, args) => {
      forgot_password_handler(e, args);
    },
    [forgot_password_handler]
  );

  const get_user_tutorials = useCallback(async () => {
    set_loading((prev) => ({ ...(prev || {}), tutorial: true }));
    const result = await get_current_user_tutorials_query();
    if (result?.status) {
      set_tutorials(result?.data);
    }
    set_loading((prev) => ({ ...(prev || {}), tutorial: false }));
  }, []);


  const validateFieldRealTime = (name, value, _validationRules = validationRules) => {
    let rule = (_validationRules)?.[name];

    if (name === "password_confirmation") {
      if (value !== form.password) return core_message("تکرار رمز عبور مطابقت ندارد.");
      return "";
    }

    if (rule && !rule.regex.test(value)) return rule.message;

    return "";
  };


  const update_user_identity_handler = useCallback(async (e, args = {}) => {
    e?.preventDefault?.();
    const { success_callback, error_callback, validations = {}, fields = {} } = args;

    // sample:
    // const validation_rules = {
    //   mobile: ["iranian_mobile", "required"],
    //   email: ["required", "email", { type: "min_length", length: 3 }],
    //   name: ["required", { type: "min_length", length: 3 }],
    //   last_name: ["required", { type: "min_length", length: 3 }],
    //   national_code: ["required", "iranian_national_code"],
    // }

    let { has_error, _errors } = await check_validation_with_form(
      validations,
      form
    );
    set_form((prev) => ({ ...prev, errors: _errors }));
    if (!has_error) {
      set_loading((prev) => ({ ...prev, update_user: true }));


      const result = await update_user_identity_mutation({
        input: {
          ...fields
        },
      });
      if (result?.status) {
        // user_information is merged into the current one: sending { user_information: { nickname } } used to drop the
        // user's other user_information keys until the user was read again (jument)
        set_user(prev => ({
          ...prev,
          ...fields,
          ...(fields?.user_information && typeof fields.user_information === "object"
            ? { user_information: { ...(prev?.user_information || {}), ...fields.user_information } }
            : {}),
        }));

        if (is_function(success_callback)) {
          success_callback(result?.data);
        }
      } else {
        const _errors = {}
        Object.keys(result?.errors?.[0]?.errors || {})?.forEach(key => {
          _errors[key] = result?.errors?.[0]?.errors?.[key]?.map(item => `validations.${item}`)
        })
        set_form(prev => ({ ...prev, errors: _errors }))
        if (is_function(error_callback)) {
          error_callback(result?.errors);
        }
      }

      set_loading((prev) => ({ ...prev, update_user: false }));

    }
  }, [form]);


  const update_user_mobile_send_sms_handler = useCallback(async (e, args) => {
    const validations = {
      mobile: ["iranian_mobile", "required"]
    }

    let { has_error, _errors } = await check_validation_with_form(
      validations,
      form
    );

    if (has_error) {
      set_form((prev) => ({ ...prev, errors: _errors }));
    } else {

      const { success_callback, error_callback } = args;

      set_loading((prev) => ({ ...prev, update_user_mobile: true }));

      const result = await update_user_mobile_send_sms_mutation({ mobile: form?.mobile });

      if (result?.status) {
        if (is_function(success_callback)) {
          success_callback(result?.data);
        }
      } else {
        const _errors = {}
        Object.keys(result?.errors?.[0]?.errors || {})?.forEach(key => {
          _errors[key] = result?.errors?.[0]?.errors?.[key]?.map(item => `validations.${item}`)
        })
        set_form(prev => ({ ...prev, errors: _errors }))
        if (is_function(error_callback)) {
          error_callback(result?.errors);
        }
      }

      set_loading((prev) => ({ ...prev, update_user_mobile: false }));

    }
  }, [form?.mobile]);

  const update_user_mobile_verify_sms_handler = useCallback(async (e, args) => {
    const validations = {
      code: ["required"]
    }

    let { has_error, _errors } = await check_validation_with_form(
      validations,
      form
    );

    if (has_error) {
      set_form((prev) => ({ ...prev, errors: _errors }));
    } else {

      const { success_callback, error_callback } = args;

      set_loading((prev) => ({ ...prev, update_user_mobile: true }));

      const result = await update_user_mobile_verify_sms_mutation({ mobile: form?.mobile, code: form?.code });

      if (result?.status) {
        set_user(prev => ({
          ...prev,
          mobile: form?.mobile
        }));
        if (is_function(success_callback)) {
          success_callback(result?.data);
        }
      } else {
        const _errors = {}
        Object.keys(result?.errors?.[0]?.errors || {})?.forEach(key => {
          _errors[key] = result?.errors?.[0]?.errors?.[key]?.map(item => `validations.${item}`)
        })
        set_form(prev => ({ ...prev, errors: _errors }))
        if (is_function(error_callback)) {
          error_callback(result?.errors);
        }
      }

      set_loading((prev) => ({ ...prev, update_user_mobile: false }));

    }
  }, [form?.mobile, form?.code]);


  /*
   * 3 step mobile change flow, which proves ownership of BOTH numbers:
   *
   *   step 1  update_user_mobile_start_handler           code -> current mobile
   *   step 2  update_user_mobile_verify_current_handler  that code + the new number,
   *                                                      code -> new mobile
   *   step 3  update_user_mobile_confirm_handler         the new number's code
   *
   * The v1 pair above (update_user_mobile_send_sms_handler /
   * update_user_mobile_verify_sms_handler) is left untouched for the sites that
   * still talk to the single step api.
   *
   * Every handler reads its values from the form, so a caller with its own field
   * names (new_mobile, current_code, ...) passes them through mobile_field /
   * code_field and gets the api errors mapped back onto those same names.
   */

  // the ui may keep the code masked ("1 2 3 4"), the api wants the digits
  const clean_sms_code = (code) => String(code ?? "").replace(/[^0-9]/g, "");

  // extra_data is a json scalar: depending on the api it arrives already parsed
  // or as a raw string
  const parse_extra_data = (extra_data) => {
    if (typeof extra_data !== "string") return extra_data || {};
    try {
      return JSON.parse(extra_data) || {};
    } catch (error) {
      return {};
    }
  };

  // the api reports validation failures as arrays ({ mobile: ["..."] }) but
  // explicit checks as plain strings ({ code: "incorrect||code" }), so both
  // shapes have to be handled here
  const set_mobile_flow_errors = useCallback((errors, error_fields = {}) => {
    const api_errors = errors?.[0]?.errors || {};
    const _errors = {};

    Object.keys(api_errors)?.forEach((key) => {
      const value = api_errors?.[key];
      const messages = (Array.isArray(value) ? value : [value])?.filter(Boolean);
      if (messages?.length > 0) {
        _errors[error_fields?.[key] || key] = messages?.map(
          (item) => `validations.${item}`
        );
      }
    });

    set_form((prev) => ({
      ...(prev || {}),
      errors: { ...(prev?.errors || {}), ..._errors },
    }));

    return _errors;
  }, []);

  const mobile_flow_error_handler = useCallback(
    (result, args = {}, error_fields = {}) => {
      const { error_callback } = args;
      const field_errors = set_mobile_flow_errors(result?.errors, error_fields);

      if (is_function(error_callback)) {
        // 401 authenticate_error and 403 throttle_error carry no field at all,
        // so the ui needs the bare message as a fallback
        error_callback(result?.errors, {
          field_errors,
          message: result?.errors?.[0]?.message,
          code: result?.errors?.[0]?.code,
        });
      }
    },
    [set_mobile_flow_errors]
  );

  // step 1: send a code to the number the user already has. Takes no mobile
  // argument. success_callback receives current_mobile_verification_required,
  // which is false when the account has no mobile yet (registered by email or
  // username): nothing was sent and step 2 must not ask for a code.
  const update_user_mobile_start_handler = useCallback(async (e, args = {}) => {
    e?.preventDefault?.();
    const { success_callback } = args;

    set_loading((prev) => ({ ...prev, update_user_mobile_start: true }));

    const captcha = await get_captcha_token({ place: "change-mobile" });
    const result = await update_user_mobile_send_sms_v2_mutation({ captcha });

    if (result?.status) {
      const extra_data = parse_extra_data(result?.data?.extra_data);
      // gate the resend button the same way the login flow does
      set_counter_down(120);
      if (is_function(success_callback)) {
        success_callback({
          ...(result?.data || {}),
          current_mobile_verification_required:
            !!extra_data?.current_mobile_verification_required,
        });
      }
    } else {
      mobile_flow_error_handler(result, args);
    }

    set_loading((prev) => ({ ...prev, update_user_mobile_start: false }));
  }, [mobile_flow_error_handler, get_captcha_token]);

  // step 2: verify the current number's code and submit the new one, which then
  // receives a code of its own. The step 1 code is not consumed here, so a
  // mistyped new number can simply be resubmitted.
  const update_user_mobile_verify_current_handler = useCallback(async (e, args = {}) => {
    e?.preventDefault?.();
    const {
      success_callback,
      mobile_field = "mobile",
      code_field = "code",
      current_mobile_verification_required = true,
    } = args;

    const validations = {
      [mobile_field]: ["required", "iranian_mobile"],
      ...(current_mobile_verification_required ? { [code_field]: ["required"] } : {}),
    };

    const { has_error, _errors } = await check_validation_with_form(validations, form);

    if (has_error) {
      set_form((prev) => ({ ...prev, errors: _errors }));
      return;
    }

    // the api accepts the number the user already has (it just re-verifies it),
    // but this flow exists to CHANGE the mobile, so that is always a mistake
    if (user?.mobile && form?.[mobile_field]?.trim() === user?.mobile?.trim()) {
      set_form((prev) => ({
        ...prev,
        errors: {
          ...(prev?.errors || {}),
          [mobile_field]: core_message("شماره موبایل جدید نباید با شماره فعلی یکسان باشد."),
        },
      }));
      return;
    }

    set_loading((prev) => ({ ...prev, update_user_mobile_verify_current: true }));

    const captcha = await get_captcha_token({ place: "change-mobile" });
    const result = await update_user_mobile_verify_current_sms_mutation({
      captcha,
      mobile: form?.[mobile_field],
      code: current_mobile_verification_required
        ? clean_sms_code(form?.[code_field])
        : undefined,
    });

    if (result?.status) {
      // the new number just got its own code, so restart the resend gate
      set_counter_down(120);
      if (is_function(success_callback)) {
        success_callback(result?.data);
      }
    } else {
      mobile_flow_error_handler(result, args, {
        mobile: mobile_field,
        code: code_field,
      });
    }

    set_loading((prev) => ({ ...prev, update_user_mobile_verify_current: false }));
  }, [form, user?.mobile, mobile_flow_error_handler, get_captcha_token]);

  // step 3: confirm the code that arrived at the new number. The mobile has to
  // be the same one submitted at step 2.
  const update_user_mobile_confirm_handler = useCallback(async (e, args = {}) => {
    e?.preventDefault?.();
    const { success_callback, mobile_field = "mobile", code_field = "code" } = args;

    const validations = { [code_field]: ["required"] };

    const { has_error, _errors } = await check_validation_with_form(validations, form);

    if (has_error) {
      set_form((prev) => ({ ...prev, errors: _errors }));
      return;
    }

    set_loading((prev) => ({ ...prev, update_user_mobile_confirm: true }));

    const result = await update_user_mobile_verify_sms_mutation({
      mobile: form?.[mobile_field],
      code: clean_sms_code(form?.[code_field]),
    });

    if (result?.status) {
      set_user((prev) => ({ ...prev, mobile: form?.[mobile_field] }));
      if (is_function(success_callback)) {
        success_callback(result?.data);
      }
    } else {
      mobile_flow_error_handler(result, args, {
        mobile: mobile_field,
        code: code_field,
      });
    }

    set_loading((prev) => ({ ...prev, update_user_mobile_confirm: false }));
  }, [form, mobile_flow_error_handler]);


  return {
    register_with_email,
    has_been_registered,
    register_send_sms_handler,
    login_send_sms_handler,
    resend_code,
    check_user_has_registered,
    login_handler,
    register_handler,
    sms_verify_handler,
    check_has_user_handler,
    submit_with_sms_handler,
    submit_with_email_handler,
    forgot_password_handler,
    reset_password_verify_handler,
    counter_down,
    convert_countdown_time,
    form,
    set_form,
    form_on_change,
    logout_process,
    update_user_handler,
    get_user_handler,
    submit_change_password_handler,
    loading,
    user_loading,
    resend_email_code,
    get_user_tutorials,
    tutorials,
    login_with_email_handler,
    login_or_register_send_sms_handler,
    submit_login_or_register_with_sms_handler,
    register_with_email_handler,
    reset_password_send_sms_handler,
    company,
    update_company_handler,
    validateFieldRealTime,
    update_user_identity_handler,
    init_birtyday,
    birthday_validation,
    update_user_mobile_send_sms_handler,
    update_user_mobile_verify_sms_handler,
    update_user_mobile_start_handler,
    update_user_mobile_verify_current_handler,
    update_user_mobile_confirm_handler
  };
};

export default useAuth;
